import {
	SAVE_FAILED,
	SAVE_INVALID,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { getPersonTaxInformation } from '@dealstack/frontend/app/services/Person.service.ts';
import { updateInvestorCustomDetails } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { ERROR_CODES } from '@dealstack/shared/lib/common/errors';
import { isTinInUseError } from '@dealstack/frontend/investor/pages/Investment/common/taxErrors.utils';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { INVESTOR_BACKUP_PROPERTY } from '@dealstack/frontend/investor/pages/Investment/common/useBackupController';
import { investmentJoinAndAllocationContext } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/InvestmentJoinAndAllocation';
import { DataCustomDetailTct } from '@dealstack/shared/lib/generated';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useIsMutating, useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { VALIDATE_TIN_MUTATION_KEY } from '@dealstack/frontend/shared/components/SingleTaxDetails/SingleTaxDetails';
import { CustomDetailDataTypeEnum } from '@dealstack/shared/lib/generated/models/CustomDetailDataTypeEnum.ts';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';
import {
	createPersonTaxInfoRequest,
	deletePersonTaxInfoRequest,
	updatePersonTaxInfoRequest,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/apis/useTaxInformation';
import {
	getEmploymentEmployers,
	getPersonEmployment,
	updatePersonNatural,
	upsertPersonEmployment,
} from '@dealstack/frontend/app/services/Person.service';
import { shouldIncludeInOtherDetails } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/utils/otherDetails';
import {
	createEmptyTaxResidence,
	diffTaxResidences,
	fromPersonTaxInformationList,
	TAX_RESIDENCES_FIELD,
} from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationOtherDetails/taxResidences.utils';
import {
	EMPLOYEE_ID_FIELD,
	EMPLOYER_NAME_FIELD,
} from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationOtherDetails/employment.utils';
import { OtherDetailsFormValues } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationOtherDetails/useJoinAndAllocationOtherDetails.types';

export const useJoinAndAllocationOtherDetails = () => {
	const updateInvestorCustomDetailsMutation = useMutation({
		mutationFn: (variables: Parameters<typeof updateInvestorCustomDetails>[0]) =>
			updateInvestorCustomDetails(variables),
	});

	const commonYup = useCommonYupValidation();
	const tInvestor = useTranslationInvestor();
	const {
		setIsContinueDisabled,
		setIsLoading,
		getInvestorCustomDetailsRequest,
		isPIVInvestment,
		updateBackupData,
		dataBackup,
		getInvestorDataRequest,
		kycSetting,
	} = useContext(investmentJoinAndAllocationContext);

	const personData = getInvestorDataRequest.response?.payload?.person;
	const personId = personData?.id;

	const { data: personTaxInformation, refetch: refetchPersonTaxInformation } = useQuery({
		queryKey: ['personTaxInformation', personId],
		queryFn: () => (personId ? getPersonTaxInformation(personId) : Promise.resolve([])),
		enabled: !!personId && !!kycSetting?.isTaxResidenceRequired,
		refetchOnWindowFocus: false,
	});

	const savedTaxResidences = useMemo(() => personTaxInformation ?? [], [personTaxInformation]);

	const showEmployment = !!kycSetting?.isEmployeeIdRequired;

	const personEmploymentQuery = useQuery({
		queryKey: [API_PATHS.PERSON_PERSONID_EMPLOYMENT_GET, personData?.id],
		queryFn: () => getPersonEmployment(personData?.id ?? ''),
		enabled: showEmployment && !!personData?.id,
		refetchOnWindowFocus: false,
	});
	// The GET orders primary-first, then latest — exactly the record this step edits.
	const employmentData = personEmploymentQuery.data?.[0];

	const employersQuery = useQuery({
		queryKey: [API_PATHS.EMPLOYMENT_EMPLOYERS_GET],
		queryFn: () => getEmploymentEmployers(),
		enabled: showEmployment,
		refetchOnWindowFocus: false,
	});
	const employerOptions = useMemo(() => {
		const names = (employersQuery.data ?? []).map((employer) => employer.displayName);
		return [...new Set(names)];
	}, [employersQuery.data]);

	// Exclude predefined types (TIN, TAX_RESIDENCY, COUNTRY_OF_BIRTH) — they are driven by kycSetting
	const filteredOtherDetails = useMemo(() => {
		const payload = getInvestorCustomDetailsRequest.data;
		if (!payload) {
			return null;
		}

		const isPredefinedType = (dataType: string) =>
			dataType === CustomDetailDataTypeEnum.TIN ||
			dataType === CustomDetailDataTypeEnum.TAX_RESIDENCY ||
			dataType === CustomDetailDataTypeEnum.COUNTRY_OF_BIRTH;

		const definitions = payload.definitions?.filter((field) => {
			if (field.dataType === CustomDetailDataTypeEnum.BANK_ACCOUNT) {
				return false;
			}
			if (field.dataType === CustomDetailDataTypeEnum.FORM) {
				return false;
			}
			if (field.dataType === CustomDetailDataTypeEnum.PRE_FILLABLE_FORM) {
				return false;
			}
			if (!shouldIncludeInOtherDetails(field.type, isPIVInvestment)) {
				return false;
			}
			return !isPredefinedType(field.dataType);
		});

		const values = payload.values?.filter((values) =>
			definitions.find((field) => field.id === values.customDetailId)
		);

		return { definitions, values };
	}, [getInvestorCustomDetailsRequest.data, kycSetting, isPIVInvestment]);

	const shapeWithDynamicFields = useMemo(() => {
		const fields = filteredOtherDetails?.definitions;

		const entryShape: Record<string, Yup.StringSchema> = {
			taxResidency: commonYup.string.required,
		};
		if (kycSetting?.isTinRequired) {
			entryShape['tin'] = commonYup.string.required;
		}
		const taxResidencesSchema = Yup.array().of(Yup.object().shape(entryShape));

		const shape: Record<
			string,
			| typeof commonYup.string.required
			| typeof commonYup.stringArray.required
			| typeof taxResidencesSchema
		> = {};

		if (fields) {
			for (const field of fields) {
				shape[field.id] = commonYup.string.required;
			}
		}

		if (kycSetting?.isTaxResidenceRequired) {
			shape[TAX_RESIDENCES_FIELD] = taxResidencesSchema;
		}
		if (kycSetting?.isPlaceOfBirthRequired) {
			shape['placeOfBirth'] = commonYup.string.required;
		}
		if (kycSetting?.isCountryOfBirthRequired) {
			shape['countryOfBirth'] = commonYup.string.required;
		}
		if (kycSetting?.isEmployeeIdRequired) {
			shape[EMPLOYER_NAME_FIELD] = commonYup.string.required;
			shape[EMPLOYEE_ID_FIELD] = commonYup.string.required;
		}
		if (kycSetting?.isCitizenshipRequired) {
			shape['citizenship'] = commonYup.stringArray.required;
		}

		return Yup.object().shape(shape);
	}, [filteredOtherDetails, kycSetting]);

	const customDetailsForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: yupResolver(shapeWithDynamicFields),
	});

	const lastInitPayload = useRef<string | null>(null);

	useEffect(() => {
		if (dataBackup.otherDetails) {
			const restored: OtherDetailsFormValues = { ...dataBackup.otherDetails };
			if (kycSetting?.isTaxResidenceRequired) {
				const savedEntries = fromPersonTaxInformationList(savedTaxResidences);
				restored[TAX_RESIDENCES_FIELD] = savedEntries.length
					? savedEntries
					: [createEmptyTaxResidence()];
			}
			const key = JSON.stringify(restored);
			if (lastInitPayload.current === key) {
				return;
			}
			customDetailsForm.reset(restored);
			lastInitPayload.current = key;
			return;
		}

		if (!filteredOtherDetails || !personData || !kycSetting) {
			return;
		}

		const customDetailsFields: OtherDetailsFormValues =
			filteredOtherDetails.definitions.reduce<OtherDetailsFormValues>(
				(acc, fieldDefinition) => {
					const fieldValue = filteredOtherDetails.values.find(
						(value) => value.customDetailId === fieldDefinition.id
					)?.value;
					return { ...acc, [fieldDefinition.id]: fieldValue };
				},
				{}
			);

		if (kycSetting?.isTaxResidenceRequired) {
			const savedEntries = fromPersonTaxInformationList(savedTaxResidences);
			customDetailsFields[TAX_RESIDENCES_FIELD] = savedEntries.length
				? savedEntries
				: [createEmptyTaxResidence()];
		}
		if (kycSetting?.isPlaceOfBirthRequired) {
			customDetailsFields.placeOfBirth = personData?.placeOfBirth ?? undefined;
		}
		if (kycSetting?.isCountryOfBirthRequired) {
			customDetailsFields.countryOfBirth = personData?.countryOfBirth ?? undefined;
		}
		if (kycSetting?.isEmployeeIdRequired) {
			customDetailsFields[EMPLOYER_NAME_FIELD] = employmentData?.employerName ?? undefined;
			customDetailsFields[EMPLOYEE_ID_FIELD] = employmentData?.employeeReference ?? undefined;
		}
		if (kycSetting?.isCitizenshipRequired) {
			customDetailsFields.citizenship = personData?.citizenship ?? undefined;
		}

		const key = JSON.stringify(customDetailsFields);
		if (lastInitPayload.current === key) {
			return;
		}
		customDetailsForm.reset(customDetailsFields);
		lastInitPayload.current = key;
	}, [filteredOtherDetails, savedTaxResidences, personData, kycSetting, employmentData]);

	const isValidatingTin = useIsMutating({
		mutationKey: [VALIDATE_TIN_MUTATION_KEY],
	});

	useEffect(() => {
		setIsContinueDisabled(
			!!isValidatingTin || !!Object.entries(customDetailsForm.formState.errors).length
		);
	}, [customDetailsForm.formState, isValidatingTin, setIsContinueDisabled]);

	const showTaxDetails = !!(kycSetting?.isTinRequired && kycSetting?.isTaxResidenceRequired);

	const showTaxResidencyOnly = !!(
		kycSetting?.isTaxResidenceRequired && !kycSetting?.isTinRequired
	);

	const showTaxSection = showTaxDetails || showTaxResidencyOnly;

	const showPlaceOfBirth = !!kycSetting?.isPlaceOfBirthRequired;

	const showCountryOfBirth = !!kycSetting?.isCountryOfBirthRequired;

	const showCitizenship = !!kycSetting?.isCitizenshipRequired;

	const onSubmit = useCallback(
		async (formValues: OtherDetailsFormValues) => {
			setIsLoading(true);

			const stringValue = (fieldName: string): string | undefined => {
				const value = formValues[fieldName];
				return typeof value === 'string' ? value : undefined;
			};

			try {
				const requiresPerson =
					showTaxSection ||
					showTaxResidencyOnly ||
					showPlaceOfBirth ||
					showCountryOfBirth ||
					showEmployment ||
					showCitizenship;
				if (requiresPerson && !personId) {
					showToastV2({
						title: tInvestor('investment.toast.failed_save_personal_information'),
						state: 'error',
					});
					throw new Error('Cannot save personal details: investor profile not loaded');
				}

				const saveTaxData = async () => {
					if (!personId || !showTaxSection) {
						return;
					}
					const { toCreate, toUpdate, toDeleteIds } = diffTaxResidences(
						savedTaxResidences,
						formValues[TAX_RESIDENCES_FIELD] ?? [],
						showTaxDetails
					);
					await Promise.all(
						toDeleteIds.map((taxId) => deletePersonTaxInfoRequest({ personId, taxId }))
					);
					await Promise.all([
						...toCreate.map((body) => createPersonTaxInfoRequest({ personId, body })),
						...toUpdate.map(({ taxId, body }) =>
							updatePersonTaxInfoRequest({ personId, taxId, body })
						),
					]);
				};

				const savePersonDetails = async () => {
					const placeOfBirth = showPlaceOfBirth ? stringValue('placeOfBirth') : undefined;
					const countryOfBirth = showCountryOfBirth
						? stringValue('countryOfBirth')
						: undefined;
					const citizenshipValue = formValues.citizenship;
					const citizenship =
						showCitizenship &&
						Array.isArray(citizenshipValue) &&
						citizenshipValue.length
							? citizenshipValue
							: undefined;
					if (!personId || (!placeOfBirth && !countryOfBirth && !citizenship)) {
						return;
					}
					await updatePersonNatural({
						personId,
						updateArgs: {
							firstName: personData?.firstName ?? '',
							lastName: personData?.lastName ?? '',
							...(placeOfBirth ? { placeOfBirth } : {}),
							...(countryOfBirth ? { countryOfBirth } : {}),
							...(citizenship ? { citizenship } : {}),
						},
					});
				};

				const saveEmployment = async () => {
					// The form-value union also carries arrays (tax residences); employment
					// fields are plain strings, so narrow before building the request.
					const employerName = formValues[EMPLOYER_NAME_FIELD];
					const employeeReference = formValues[EMPLOYEE_ID_FIELD];
					if (
						!personId ||
						!showEmployment ||
						typeof employerName !== 'string' ||
						!employerName ||
						typeof employeeReference !== 'string' ||
						!employeeReference
					) {
						return;
					}
					await upsertPersonEmployment({
						personId,
						updateArgs: { employerName, employeeReference },
					});
				};

				const saveCustomDetails = async () => {
					const dataToSave = filteredOtherDetails?.definitions?.map((customDetail) => ({
						customDetailId: customDetail.id,
						customDetailDataType: customDetail.dataType,
						value: stringValue(customDetail.id) ?? '',
						taxId: '',
					}));
					if (!dataToSave?.length) {
						return;
					}
					await updateInvestorCustomDetailsMutation.mutateAsync({ data: dataToSave });
				};

				try {
					await Promise.all([
						saveTaxData(),
						savePersonDetails(),
						saveEmployment(),
						saveCustomDetails(),
					]);
				} catch (error) {
					const resolveErrorTitle = () => {
						if (isTinInUseError(error)) {
							return tInvestor('investment.toast.tin_already_in_use');
						}
						const isDuplicateTaxResidence =
							isStandardBackendError(error) &&
							error.error.code === ERROR_CODES.PROJECT.CAP.PERSONS[120403];
						return isDuplicateTaxResidence
							? tInvestor('investment.toast.duplicate_tax_residence', {
									defaultValue:
										'A tax residence for this country already exists.',
								})
							: tInvestor('investment.toast.failed_save_personal_information');
					};
					showToastV2({ title: resolveErrorTitle(), state: 'error' });
					throw error;
				}
				await Promise.all([
					getInvestorDataRequest
						.fetch?.({ withDraftPersonRelations: true })
						?.concludeWithPromise(),
					getInvestorCustomDetailsRequest.refetch(),
					...(personId && showTaxSection ? [refetchPersonTaxInformation()] : []),
					...(showEmployment ? [personEmploymentQuery.refetch()] : []),
				]);
				showToastV2({ title: 'Data saved successfully!' });
			} finally {
				setIsLoading(false);
			}
		},
		[
			personData,
			personId,
			savedTaxResidences,
			refetchPersonTaxInformation,
			showTaxDetails,
			showTaxSection,
			showPlaceOfBirth,
			showCountryOfBirth,
			showEmployment,
			showCitizenship,
			filteredOtherDetails,
			tInvestor,
		]
	);

	const onSubmitRef = useRef(onSubmit);
	onSubmitRef.current = onSubmit;

	useWizardStep(async (intent) => {
		const formValues = customDetailsForm.getValues() as OtherDetailsFormValues;
		if (intent !== 'next') {
			updateBackupData(INVESTOR_BACKUP_PROPERTY.otherDetails, formValues);
			if (await customDetailsForm.trigger()) {
				try {
					await onSubmitRef.current(formValues);
				} catch {
					// save failed — keep the backup and still allow navigating back
				}
			}
			return SAVE_OK;
		}
		if (!(await customDetailsForm.trigger())) {
			return SAVE_INVALID;
		}
		updateBackupData(INVESTOR_BACKUP_PROPERTY.otherDetails, formValues);
		try {
			await onSubmitRef.current(formValues);
		} catch {
			return SAVE_FAILED;
		}
		return SAVE_OK;
	});

	const sortedFields = useMemo(() => {
		const individualFields: DataCustomDetailTct[] = [];
		const pivFields: DataCustomDetailTct[] = [];

		for (const fieldDefinition of filteredOtherDetails?.definitions ?? []) {
			if (fieldDefinition.type === CustomDetailEnum.INDIVIDUAL) {
				individualFields.push(fieldDefinition);
			} else {
				pivFields.push(fieldDefinition);
			}
		}

		return { individualFields, pivFields };
	}, [filteredOtherDetails]);

	return {
		customDetailsForm,
		onSubmit,
		isPIVInvestment,
		personalFields: sortedFields.individualFields,
		pivFields: sortedFields.pivFields,
		savedTaxResidences,
		showTaxDetails,
		showTaxSection,
		showPlaceOfBirth,
		showCountryOfBirth,
		showEmployment,
		employerOptions,
		showCitizenship,
		tinFieldName: 'tin',
		taxResidencyFieldName: 'taxResidency',
		placeOfBirthFieldName: 'placeOfBirth',
		countryOfBirthFieldName: 'countryOfBirth',
		citizenshipFieldName: 'citizenship',
	};
};
